<script lang="ts" setup>
import { computed } from 'vue'
import type { MemberPageConfig } from '../configs/types'
import { useUserStore } from '@/stores'
import { useSubscription } from '@/composables/payment'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'

interface Props {
  config: MemberPageConfig
}

const props = defineProps<Props>()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')

function login() {
  elementClick('login')
  tripartiteLoginPage('Profile')
}

// 样式变量
const userBgUrl = `url(${props.config.resources.userBg})`
const vipSubscribeBgUrl = props.config.theme.vipSubscribeBg
const userNameColor = computed(() => props.config.theme.userNameColor || '#333333')
const userIdColor = computed(() => props.config.theme.userIdColor || '#999999')
</script>

<template>
  <div class="cover">
    <!-- 用户信息区域 -->
    <div class="px-20px pt-50px pb-20px flex items-center justify-between">
      <div class="flex items-center space-x-12px">
        <img
          v-if="userStore.$state.headImageUrl"
          class="w-60px h-60px rounded-full border-2 border-white"
          :src="userStore.$state.thirdHeadImgUrl"
          alt="avatar"
        >
        <img
          v-else
          class="w-60px h-60px rounded-full border-2 border-white"
          :src="config.resources.userDefaultAvatar"
          alt="avatar"
        >
        <div>
          <p
            class="text-18px font-medium overflow-hidden whitespace-nowrap text-ellipsis"
            :class="{ 'w-200px': userStore.$state.email }"
            :style="{ color: userNameColor }"
          >
            {{ userStore.$state.thirdNickName || userStore.$state.email || userStore.$state.memberName || 'user' }}
          </p>
          <p class="text-13px mt-4px" :style="{ color: userIdColor }">
            UID: {{ userStore.$state.code }}
          </p>
        </div>
      </div>
      <div v-if="config.showLoginBtn !== false && !userStore.$state.thirdPartType" class="flex items-center text-14px" :style="{ color: userIdColor }" @click="login">
        <span>{{ config.texts.signIn }}</span>
        <i-icon-park-outline-right class="text-12px ml-2px" />
      </div>
    </div>

    <!-- VIP 会员卡片 -->
    <div v-if="subscribeProducts?.length || userStore.$state.subscribe" class="mx-16px mt-10px">
      <div class="vip-cover flex items-center justify-between py-14px pr-16px">
        <div class="pl-50px pr-10px">
          <div class="text-16px font-medium text-[#8B6914]">
            <span v-if="!userStore.$state.subscribe" class="line-clamp-1">{{ config.texts.vipTitle || config.texts.getVip }}</span>
            <span v-else>{{ config.texts.vipMembership }}</span>
          </div>
          <div v-if="!userStore.$state.subscribe" class="text-12px text-[#B8956A] mt-2px">
            {{ config.texts.vipSubtitle || config.texts.moreCoinsMoreBenefits }}
          </div>
          <div v-if="userStore.$state.subscribeEndDate" class="text-12px text-[#B8956A] mt-2px">
            {{ config.texts.expires }} {{ $d(new Date(userStore.$state.subscribeEndDate), 'short') }}
          </div>
        </div>
        <router-link
          v-if="!userStore.$state.subscribe"
          :to="{ name: 'subscribe' }"
        >
          <div class="px-16px py-6px text-13px font-medium text-white rounded-full btn-details" @click="elementClick('Details')">
            {{ config.texts.details }}
          </div>
        </router-link>
      </div>
    </div>

    <!-- 菜单列表 -->
    <div class="mt-16px mx-16px px-12px rounded-12px bg-white">
      <div>
        <router-link
          :to="{ name: 'recharge' }"
          class="w-full h-56px flex items-center justify-between"
        >
          <div class="flex items-center space-x-12px">
            <img v-if="config.menuIcons?.transaction" :src="config.menuIcons.transaction" class="w-22px h-22px">
            <i-assets-icon-transaction v-else class="text-n-primary" />
            <p class="text-15px text-[#333333]">
              {{ config.texts.transactionHistory }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3 text-14px" />
        </router-link>
      </div>
      <div>
        <router-link
          :to="{ name: 'consume' }"
          class="w-full h-56px flex items-center justify-between border-t border-[#F3F5FA]"
        >
          <div class="flex items-center space-x-12px">
            <img v-if="config.menuIcons?.unlocked" :src="config.menuIcons.unlocked" class="w-22px h-22px">
            <i-assets-icon-unlocked v-else class="text-n-primary" />
            <p class="text-15px text-[#333333]">
              {{ config.texts.chaptersUnlocked }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3 text-14px" />
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'contact-us' }">
          <div class="w-full h-56px flex items-center justify-between border-t border-[#F3F5FA]" @click="elementClick('contact_us')">
            <div class="flex items-center space-x-12px">
              <img v-if="config.menuIcons?.contactUs" :src="config.menuIcons.contactUs" class="w-22px h-22px">
              <i-assets-icon-contact-us v-else class="text-n-primary" />
              <p class="text-15px text-[#333333]">
                {{ config.texts.contactUs }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3 text-14px" />
          </div>
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'setting' }">
          <div class="w-full h-56px flex items-center justify-between border-t border-[#F3F5FA]" @click="elementClick('setting')">
            <div class="flex items-center space-x-12px">
              <img v-if="config.menuIcons?.setting" :src="config.menuIcons.setting" class="w-22px h-22px">
              <i-assets-icon-setting v-else class="text-n-primary" />
              <p class="text-15px text-[#333333]">
                {{ config.texts.setting }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3 text-14px" />
          </div>
        </router-link>
      </div>
    </div>

    <BasicTabbar tab="member" />
  </div>
</template>

<style scoped>
.cover {
  width: 100%;
  min-height: 100vh;
  background: v-bind(userBgUrl) no-repeat top left;
  background-size: 100% auto;
  background-color: #F6F7F9;
}

.vip-cover {
  width: 100%;
  height: 72px;
  background: v-bind(vipSubscribeBgUrl) no-repeat top left;
  background-size: 100% 100%;
  border-radius: 12px;
}

.btn-details {
  background: linear-gradient(90deg, #D49269 0%, #B76C41 100%);
}
</style>
